import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tree, DndProvider, getDescendants } from '@minoru/react-dnd-treeview';
import { resolvePipeHeight, sortTreeDataAfterMove } from './utils';
import { countSiblingsWithSameParent, filterPermissionBasedContent } from '../../utils/common';
import { OPEN_API_POPOVER_OPTIONS } from '../../constants/common';
import { ExplorerIcon, DropdownIcon } from './ExplorerIcon';
import { HTML5Backend } from 'react-dnd-html5-backend';
import PopoverMenu from '../PopoverMenu/PopoverMenu';
import { useTransformer } from './hooks';
import {
  updateSidebarPosition,
  moveFileToDestination,
  updateSelectionPathOpenApi,
} from './../../features/organization/organizationSlice';
import { MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Tag, Typography } from 'antd';
import Spinner from '../Spinner';
import './index.css';
import useDocumentCreation from '../../hooks/useDocumentCreation';
import { RightOutlined } from '@ant-design/icons';
import useChangeNavigationOnActiveFile from '../../hooks/useChangeNavigationOnActiveFile';
import { useFileNavigationContext } from '../../contexts/FileNavigationContext';

const TREE_X_OFFSET = 20;

const filterNodeOptions = (contents, isGitBased, ignoreSlugs) => {
  contents = contents.filter((content) => {
    if (isGitBased) return !ignoreSlugs.includes(content.slug);
    return true;
  });
  return contents;
};

export const Placeholder = memo(({ depth }) => (
  <div
    style={{
      position: 'absolute',
      top: 0,
      right: 0,
      height: 1,
      left: depth * 24,
      transform: 'translateY(-50%)',
      backgroundColor: 'var(--primary-color)',
      zIndex: 100,
    }}
  />
));

/**
 * A render function for individual tree nodes in the file explorer.
 *
 * @param {{ node: import('./types').FileExplorerNode, depth: number, isOpen: boolean, onToggle: () => void }}
 * @returns {JSX.Element}
 */
export const FileContextItem = ({
  node,
  depth,
  isOpen,
  isDropTarget,
  onClick,
  treeData,
  getPipeHeight,
  isLoading,
  fileOptions,
  folderOptions,
  gitFolderOptionsToHide,
  gitFileOptionsToHide,
  workspaceId,
  userPermissions,
  isGitBased,
  // currentSelected,
}) => {
  const { currentSelected } = useFileNavigationContext();
  const indent = depth * TREE_X_OFFSET;
  const isfolder = !node?.isFile && !node?.isApi;
  const { createDocument } = useDocumentCreation();

  let filteredNodeOptions = filterNodeOptions(
    isfolder ? folderOptions : fileOptions,
    isGitBased,
    isfolder ? gitFolderOptionsToHide : gitFileOptionsToHide
  );
  if (userPermissions?.username)
    filteredNodeOptions = filterPermissionBasedContent(
      filteredNodeOptions,
      userPermissions?.username,
      userPermissions,
      workspaceId
    );

  if (node?.isApi)
    filteredNodeOptions = filteredNodeOptions.filter((item) => OPEN_API_POPOVER_OPTIONS.includes(item?.slug));

  const { prefixIcon } = useMemo(() => {
    let key = node?.icon ? node?.icon : node?.meta_info?.icon ? node?.meta_info?.icon : isfolder ? 'folder' : 'file';
    if ((isfolder || (node?.isApi && !node.leaf)) && isOpen && key == 'folder') key += '-open';

    const icon = <ExplorerIcon nodeType={node?.icon_id || key} />;
    return { prefixIcon: icon };
  }, [node, isOpen, isfolder]);

  const isPopoverEmpty = !filteredNodeOptions?.length;
  let isSelected = currentSelected === String(node?.ref_id) || currentSelected === node?.id;
  if (isfolder) {
    isSelected =
      isSelected ||
      String(node?.meta_info?.default_file) === currentSelected ||
      String(node?.meta_info?.default_file_ref_id) === String(currentSelected);
  }

  const onClickHandler = useCallback(
    (e) => {
      e.stopPropagation();
      onClick?.(node.id, { node, isOpen });
    },
    [node, isOpen, onClick]
  );

  return (
    <div
      className={`file-context-item tree-node ${node.droppable && isDropTarget ? 'droptarget' : ''} ${isSelected ? 'selected' : ''}`}
      style={{ marginInlineStart: indent }}
      onClick={onClickHandler}
    >
      {(isfolder || (node?.isApi && !node?.leaf)) && isLoading ? (
        <Spinner color={isSelected ? 'white' : 'inherit'} size={16} />
      ) : (
        <RightOutlined
          rotate={isOpen ? 90 : 0}
          className={`dropdown-icon ${isfolder || (node?.isApi && !node?.leaf) ? 'show' : 'hide'}`}
          style={{ fontSize: '10px' }}
        />
      )}
      <div className="context-item-wrapper">
        {prefixIcon && <div className="file-icon-wrapper">{prefixIcon}</div>}

        <div className="file-text-wrapper">
          <Typography.Text style={{ color: 'inherit' }}>{node?.text}</Typography.Text>
        </div>
        {!isPopoverEmpty && (
          <div className="file-options-wrapper">
            {!node?.isApi && (
              <PlusOutlined
                onClick={(e) => {
                  e.stopPropagation();
                  createDocument(node.uuid_id);
                }}
              />
            )}
            <PopoverMenu
              content={filteredNodeOptions}
              arrow={false}
              scroll="true"
              placement="right"
              id={isGitBased ? node?.path : node?.uuid_id}
            >
              <MoreOutlined onClick={(e) => e.stopPropagation()} className="option-button" />
            </PopoverMenu>
          </div>
        )}
      </div>
      {node?.parent !== workspaceId && (
        <>
          <div className={`context-pipe-x`} style={{ width: depth > 0 ? TREE_X_OFFSET - 9 : 0 }} />
          {getDescendants(treeData, node.parent)[0].id === node.id && (
            <div
              className={'context-pipe-y'}
              style={{
                height: Math.max(0, getPipeHeight(node.parent, treeData) - 8),
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

/**
 * Renders a drag-and-drop enabled file explorer tree based on the given data.
 *
 * @param {{ data: import('./types').FileExplorerData }} props
 * @returns {JSX.Element}
 */
export const FileExplorer = React.memo(
  ({
    explorerRootId,
    data,
    onMenuClick,
    isFolderLoading,
    folderOptions,
    fileOptions,
    gitFolderOptionsToHide,
    gitFileOptionsToHide,
    userPermissions,
    isOrganizationAdmin,
    isGitBased,
    openApiClickHandler,
    currentSelected,
    setCurrentSelected,
    expandedKeys,
    setCurrentExpandedKeys,
  }) => {
    const ref = useRef(null);
    const dndRef = useRef(null);
    const dispatch = useDispatch();
    const transfomer = useTransformer();
    const { navigateToApiMethod } = useChangeNavigationOnActiveFile();
    const activeWorkspace = useSelector((state) => state.organization?.activeWorkspace);
    const folderMappings = useSelector((state) => state.organization?.folderMappings);
    const [lastOpenedFolder, setLastOpenedFolder] = useState(null);
    const [treeData, setTreeData] = useState(transfomer(data, explorerRootId));

    const [dndArea, setDnDArea] = useState(dndRef.current);
    useEffect(() => {
      setDnDArea(dndRef?.current);
    }, [dndRef]);
    const html5Options = useMemo(() => ({ rootElement: dndArea }), [dndArea]);
    const backendContext = useMemo(
      () => ({
        clearTimeout: window.clearTimeout.bind(window),
        document,
      }),
      []
    );

    useEffect(() => {
      setTreeData(transfomer(data, explorerRootId));
    }, [data, transfomer]);

    const open = useCallback(
      (id) => {
        ref.current?.open(id);
        setCurrentExpandedKeys((p) => {
          const ids = [...p];
          if (!ids.includes(id)) ids.push(id);
          return ids;
        });
      },
      [ref.current, setCurrentExpandedKeys]
    );

    const close = useCallback(
      (id) => {
        ref.current?.close(id);
        setCurrentExpandedKeys((p) => p.filter((i) => i !== id));
      },
      [ref.current, setCurrentExpandedKeys]
    );

    const toggle = useCallback((id) => (expandedKeys.includes(id) ? close(id) : open(id)), [expandedKeys, open, close]);

    const getPipeHeight = useCallback((id, treeData) => resolvePipeHeight(id, treeData, expandedKeys), [expandedKeys]);

    const handleDrop = useCallback(
      (newTreeData, e) => {
        const { dragSourceId, dropTargetId, destinationIndex, relativeIndex } = e;
        if (isGitBased) return;
        if (typeof dragSourceId === 'undefined' || typeof dropTargetId === 'undefined') return;

        const { start, end } = treeData.reduce(
          (acc, node) => {
            if (node && node.id === dragSourceId) acc['start'] = node;
            if (node && node.id === dropTargetId) acc['end'] = node;
            return acc;
          },
          { start: undefined, end: undefined }
        );

        if (start?.parent === dropTargetId && start && typeof destinationIndex === 'number') {
          const newPosition = countSiblingsWithSameParent(newTreeData, destinationIndex, explorerRootId);
          // dispatch sidebar position
          // if (start?.isApi) return;
          dispatch(updateSidebarPosition({ id: start.id, position: newPosition }));
          setTreeData((prev) => sortTreeDataAfterMove(prev, prev.indexOf(start), destinationIndex));
        }

        if (start?.parent !== dropTargetId && start && typeof destinationIndex === 'number') {
          if (
            getDescendants(treeData, dragSourceId).find((el) => el.id === dropTargetId) ||
            dropTargetId === dragSourceId ||
            (end && !end?.droppable)
          )
            return;

          if (end?.isApi) return;

          // move or folder to destination with sidebar position
          dispatch(
            moveFileToDestination({ sourceId: dragSourceId, destinationId: dropTargetId, position: relativeIndex })
          );
          setTreeData((prev) => {
            const output = sortTreeDataAfterMove(prev, prev.indexOf(start), destinationIndex);
            const movedElement = output.find((el) => el.id === dragSourceId);
            if (movedElement) movedElement.parent = dropTargetId;
            return output;
          });
        }
      },
      [treeData, isGitBased]
    );

    const onClickHandler = useCallback(
      async (id, { node, isOpen }) => {
        if (node?.isApi && node?.method && node?.path) {
          dispatch(updateSelectionPathOpenApi({ method: node?.method, path: node?.path }));
          setCurrentSelected(id);

          // Use navigateToApiMethod instead of openApiClickHandler to add query params
          const apiData = folderMappings?.[node?.apiKey] || {
            ref_id: node?.apiKey,
            slug: activeWorkspace?.slug,
          };

          const methodData = {
            method: node?.method,
            path: node?.path,
            tags: node?.tag ? [node.tag] : [], // Use the tag information from the node
          };

          navigateToApiMethod({
            apiData,
            methodData,
            workspace: activeWorkspace?.slug,
          });
          return;
        }

        if (!isOpen) setLastOpenedFolder(id);
        await onMenuClick(id, { node, isOpen });
        toggle?.(id);
        setCurrentSelected(id);
      },
      [onMenuClick, toggle, treeData, setCurrentSelected, dispatch]
    );

    return (
      <div className={`file-explorer-group`} ref={dndRef}>
        {dndArea && (
          <DndProvider backend={HTML5Backend} options={html5Options} context={backendContext}>
            <div className="file-explorer">
              <Tree
                classes={{
                  root: 'file-explorer-root',
                  placeholder: 'file-explorer-placeholder',
                  dropTarget: 'file-explorer-droptarget',
                  listItem: 'file-explorer-list-item',
                }}
                ref={ref}
                sort={false}
                tree={treeData}
                onDrop={handleDrop}
                canDrop={() => true}
                canDrag={() => true}
                dropTargetOffset={5}
                insertDroppableFirst={false}
                rootId={explorerRootId}
                enableAnimateExpand={true}
                placeholderRender={(node, { depth }) => <Placeholder node={node} depth={depth} />}
                initialOpen={expandedKeys}
                render={(node, { depth, isOpen, isDropTarget }) => (
                  <FileContextItem
                    currentSelected={currentSelected}
                    node={node}
                    depth={depth}
                    isOpen={isOpen || false}
                    onClick={onClickHandler}
                    isDropTarget={isDropTarget}
                    treeData={treeData}
                    getPipeHeight={getPipeHeight}
                    isLoading={lastOpenedFolder ? lastOpenedFolder === node.id && isFolderLoading : false}
                    fileOptions={fileOptions}
                    folderOptions={folderOptions}
                    gitFileOptionsToHide={gitFileOptionsToHide}
                    gitFolderOptionsToHide={gitFolderOptionsToHide}
                    userPermissions={userPermissions}
                    workspaceId={explorerRootId}
                    isOrganizationAdmin={isOrganizationAdmin}
                    isGitBased={isGitBased}
                    explorerRootId={explorerRootId}
                  />
                )}
              />
            </div>
          </DndProvider>
        )}
      </div>
    );
  }
);

export default FileExplorer;
